'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/**
 * Structure of an abstract ast in loading job. The actual element structure
 * depends on Type.
 *
 * @export
 * @interface GSQLLoadingAstJson
 */

/**
 * GSQL interface representing one loading job.
 * - Type: 'Online' | 'Offline'
 * - JobName: loading job name
 * - GraphName: graph schema name
 * - Headers: user defined headers map. Key is header name, value is header column names array
 * - Filters: user defined filters that will be used in line rejection rules
 * - LoadingStatements: loading to vertex | edge | temp table, delete vertex | edge
 *
 * @export
 * @interface GSQLLoadingJobJson
 */

/**
 * GSQL interface representing one loading to vertex statement.
 * - TargetName: target vertex type name
 * - DataSource: loading data source
 * - Mappings: loading mapping IPEs
 * - WhereClause?: loading condition filter
 * - UsingClauses: loading using clauses
 * - Options?: loading options like ignore if exist, etc
 *
 * @export
 * @interface GSQLLoadToVertexJson
 */

/**
 * GSQL interface representing one loading to edge statement.
 * - TargetName: target vertex type name
 * - DataSource: loading data source
 * - Mappings: loading mapping IPEs
 * - WhereClause?: loading condition filter
 * - FromVertexType: loading to edge source vertex type
 * - ToVertexType: loading to edge target vertex type
 * - UsingClauses: loading using clauses
 * - Options?: loading options like ignore if exist, etc
 *
 * @export
 * @interface GSQLLoadToEdgeJson
 */

/**
 * GSQL interface representing one loading to temp table statement.
 * - TargetName: target vertex type name
 * - DataSource: loading data source
 * - Mappings: loading mapping IPEs
 * - WhereClause?: loading condition filter
 * - ColumnNames: temp table defined column names
 * - UsingClauses: loading using clauses
 * - Options?: loading options like ignore if exist, etc
 *
 * @export
 * @interface GSQLLoadToTempTableJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * GSQL interface representing one deleting vertex statement.
 * - VertexType: vertex type to delete
 * - PrimaryId: loading mapping of deleted vertex id
 * - TempTableName: temp table from where to get data source
 * - WhereClause?: loading condition filter
 * - UsingClauses: loading using clauses
 *
 * @export
 * @interface GSQLDeleteVertexJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * GSQL interface representing one deleting edge statement.
 * - EdgeType: edge type to delete
 * - FromVertexType: source vertex type of the edge to be deleted
 * - FromVertexPrimaryId: loading mapping of source vertex id of the edge to be deleted
 * - ToVertexType?: target vertex type of the edge to be deleted
 * - ToVertexPrimaryId?: loading mapping of target vertex id of the edge to be deleted
 * - TempTableName: temp table from where to get data source
 * - WhereClause?: loading condition filter
 * - UsingClauses: loading using clauses
 *
 * @export
 * @interface GSQLDeleteEdgeJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * GSQL interface representing data source.
 * - Type: 'TempTable' | 'Online' | 'File'
 * - Value?: temp table name, empty, or file path
 *
 * @export
 * @interface GSQLDataSourceJson
 */

// Currently only support basic loading mapping.
var supportedLoadingMappingTypes = ['SrcColIndex', 'SrcColName', 'Literal', 'Default', 'UDF', 'UDT', 'KvPair'];
var SUPPORTED_PROPNAMES = ['Type', 'Key', 'Value', 'Params', 'UdfName', 'UdtName', 'Fields', 'SrcKeyNames'];

/**
 * Loading mapping refer by column name.
 * - Value: column name
 * - SrcKeyNames: an array of column names to refer to nested attribute in json.
 *
 * @export
 * @interface GSQLSrcColNameJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * Loading mapping refer by column index.
 * - Value: column index
 *
 * @export
 * @interface GSQLSrcColIndexJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * Loading mapping of string literal.
 * - Value: string literal value
 *
 * @export
 * @interface GSQLLiteralJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * Loading mapping default.
 *
 * @export
 * @interface GSQLDefaultJson
 */

/**
 * Loading mapping of UDF.
 * - UdfName: UDF function name
 * - Params: parameters of UDF call
 *
 * @export
 * @interface GSQLUDFJson
 */

/**
 * Loading mapping of Map.
 * - Key: loading mapping to the Map key
 * - Value: loading mapping to the Map val
 *
 * @export
 * @interface GSQLKVPairJson
 * @extends {GSQLLoadingAstJson}
 */

/**
 * Loading mapping of UDT.
 * - UdtName: UDT tuple name
 * - Fields: loading mapping to the UDT fields
 *
 * @export
 * @interface GSQLUDTMappingJson
 * @extends {GSQLLoadingAstJson}
 */

exports.SUPPORTED_PROPNAMES = SUPPORTED_PROPNAMES;
exports.supportedLoadingMappingTypes = supportedLoadingMappingTypes;
